/**
 * The visual style guide, encoded. This file is the single source of truth for
 * every visual value; see docs/design/visual-style-guide.md for the cheat-sheet
 * of which role to use when.
 *
 * If a component needs a value this file does not provide, the role is missing.
 * Amend the guide, then encode it. Never the other way round.
 */

/* ------------------------------------------------------------------ *
 * LAYER 1 — the primitive scales (guide §2.1, §2.2, §2.3, §2.7)
 *
 * Named by position, because that is what they are. Nothing outside this
 * file consumes them: they exist so the semantic roles below have stable
 * values to point at. A component that reaches for `neutral[300]` has
 * bypassed the language (guide §1).
 * ------------------------------------------------------------------ */

const neutral = Object.freeze({
  50: "#f7f7f7",
  100: "#f2f5f5",
  200: "#e2e8e9",
  300: "#ced7d9",
  400: "#95a6a9",
  500: "#657578",
  600: "#46575c",
  700: "#314448",
  800: "#1a2b2f",
  900: "#0d1b1e",
});

const brand = Object.freeze({
  400: "#0ac8f2",
  500: "#0897b8",
  600: "#05677c",
  700: "#045567",
  800: "#034251",
});

const amber = Object.freeze({
  50: "#fbf4e6",
  200: "#ecd9a8",
  700: "#8a5a00",
});

const white = "#ffffff";

/* ------------------------------------------------------------------ *
 * LAYER 2 — the semantic roles (guide §2.4)
 * What a value means. This is the only layer components may consume.
 * ------------------------------------------------------------------ */

type TypeToken = { size: string; weight: number; lineHeight: number };

export type DesignTokens = {
  color: {
    surface: {
      default: string;
      sunken: string;
      elevated: string;
      subtle: string;
      hover: string;
      hoverStrong: string;
    };
    text: { primary: string; secondary: string; disabled: string };
    border: { default: string; strong: string };
    feedback: { skeleton: string };
    focus: { ring: string };
    brand: {
      primary: string;
      primaryAction: string;
      primaryActionHover: string;
      primaryActionPressed: string;
      onPrimary: string;
    };
    status: { success: string; error: string };
    // Tasks workflow lifecycle (J23). A four-state set no other family carries;
    // each state pairs a legible foreground with its own tint and border so the
    // status pill stays a filled pill and every foreground clears AA on its bg.
    workflow: {
      new: { fg: string; bg: string; border: string };
      doing: { fg: string; bg: string; border: string };
      worked: { fg: string; bg: string; border: string };
      bad: { fg: string; bg: string; border: string };
    };
    // Non-blocking caution beside a field; status.error is for what must be fixed.
    callout: {
      warning: { fg: string; bg: string; border: string };
    };
    category: {
      caseType: {
        surplusFunds: string;
        locateForService: string;
        piInvestigation: string;
        locateWitness: string;
        background: string;
      };
    };
  };
  type: {
    family: string;
    display: TypeToken;
    heading: TypeToken;
    subheading: TypeToken;
    body: TypeToken;
    bodyStrong: TypeToken;
    dense: TypeToken;
    denseStrong: TypeToken;
    caption: TypeToken;
  };
  space: Readonly<Record<0 | 1 | 2 | 3 | 4 | 5 | 6 | 7, string>>;
  density: { row: string; rowComfortable: string; field: string };
  radius: { sm: string; md: string; full: string };
  borderWidth: string;
  motion: { instant: string; fast: string; slow: string; easing: string };
  icon: { sm: string; default: string; lg: string };
  focus: { width: string; offset: string };
};

export const tokens: DesignTokens = Object.freeze({
  color: Object.freeze({
    surface: Object.freeze({
      default: white,
      sunken: neutral[50],
      elevated: white,
      subtle: neutral[100],
      hover: neutral[100],
      hoverStrong: neutral[200],
    }),
    text: Object.freeze({
      primary: neutral[800],
      secondary: neutral[600],
      // Fails AA at 2.53:1, deliberately and only for inactive controls. Guide
      // §2.6: WCAG 2.2 §1.4.3 exempts them, and raising it would make a disabled
      // control look enabled. Nothing the user must read is ever this.
      disabled: neutral[400],
    }),
    border: Object.freeze({ default: neutral[200], strong: neutral[300] }),
    // Two roles, one value. A chip's fill answers "how quiet is this?"; a
    // skeleton's answers "what is not here yet?". They may diverge (guide §2.4).
    feedback: Object.freeze({ skeleton: neutral[100] }),
    focus: Object.freeze({ ring: brand[500] }),
    brand: Object.freeze({
      primary: brand[500],
      primaryAction: brand[600],
      primaryActionHover: brand[700],
      primaryActionPressed: brand[800],
      onPrimary: white,
    }),
    status: Object.freeze({ success: "#1c8743", error: "#971919" }),
    workflow: Object.freeze({
      new: Object.freeze({ fg: neutral[600], bg: neutral[50], border: neutral[200] }),
      doing: Object.freeze({ fg: amber[700], bg: amber[50], border: amber[200] }),
      worked: Object.freeze({ fg: "#16683a", bg: "#eef8f2", border: "#bfe3cd" }),
      bad: Object.freeze({ fg: "#971919", bg: "#fbeceb", border: "#eec3c1" }),
    }),
    callout: Object.freeze({
      warning: Object.freeze({ fg: amber[700], bg: amber[50], border: amber[200] }),
    }),
    category: Object.freeze({
      caseType: Object.freeze({
        surplusFunds: "#8c6923",
        locateForService: "#4a7b1f",
        piInvestigation: "#326dc7",
        locateWitness: "#8c4fd3",
        background: "#c33192",
      }),
    }),
  }),

  type: Object.freeze({
    family: '"Inter", system-ui, sans-serif',
    display: Object.freeze({ size: "2rem", weight: 600, lineHeight: 1.25 }),
    heading: Object.freeze({ size: "1.5rem", weight: 600, lineHeight: 1.3 }),
    subheading: Object.freeze({ size: "1.25rem", weight: 600, lineHeight: 1.4 }),
    body: Object.freeze({ size: "1rem", weight: 400, lineHeight: 1.5 }),
    bodyStrong: Object.freeze({ size: "1rem", weight: 600, lineHeight: 1.5 }),
    dense: Object.freeze({ size: "0.875rem", weight: 400, lineHeight: 1.43 }),
    denseStrong: Object.freeze({
      size: "0.875rem",
      weight: 600,
      lineHeight: 1.43,
    }),
    caption: Object.freeze({ size: "0.75rem", weight: 400, lineHeight: 1.33 }),
  }),

  // An 8px grid. space.1 is the one deliberate exception: an icon 8px from its
  // own label reads as two things rather than one (guide §4).
  space: Object.freeze({
    0: "0",
    1: "4px",
    2: "8px",
    3: "16px",
    4: "24px",
    5: "32px",
    6: "48px",
    7: "64px",
  }),

  density: Object.freeze({ row: "40px", rowComfortable: "48px", field: "40px" }),

  radius: Object.freeze({ sm: "4px", md: "8px", full: "9999px" }),
  borderWidth: "1px",

  motion: Object.freeze({
    instant: "0ms",
    fast: "120ms",
    slow: "240ms",
    easing: "cubic-bezier(0.2, 0, 0, 1)",
  }),

  icon: Object.freeze({ sm: "16px", default: "20px", lg: "24px" }),
  focus: Object.freeze({ width: "2px", offset: "2px" }),
});
